/** @format */

import { addDays, endOfWeek, format, startOfWeek } from "date-fns";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const parseTzSlot = (date, time, locationTimezone) => {
  const parts = (date || "").split("/");
  if (parts.length !== 3) return dayjs(null);
  const isoDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
  const normalizedTime = (time || "").slice(0, 5); // "HH:mm" from "HH:mm" or "HH:mm:ss"
  try {
    const dt = dayjs.tz(`${isoDate}T${normalizedTime}`, locationTimezone);
    if (dt.isValid()) return dt;
  } catch {}
  return dayjs(null);
};

export const formatSlotTimeInLocationTimezone = (
  date,
  time,
  locationTimezone,
) => {
  if (!date || !time) return moment(time, "HH:mm:ss").format("h:mm A");
  if (!locationTimezone) return moment(time, "HH:mm:ss").format("h:mm A");

  try {
    const dt = parseTzSlot(date, time, locationTimezone);
    if (!dt.isValid()) return moment(time, "HH:mm:ss").format("h:mm A");
    const timeStr = dt.format("h:mm A");
    try {
      const abbr = new Intl.DateTimeFormat("en-US", {
        timeZone: locationTimezone,
        timeZoneName: "short",
      })
        .formatToParts(dt.toDate())
        .find((p) => p.type === "timeZoneName")?.value || "";
      return timeStr;
    } catch {
      return timeStr;
    }
  } catch {
    return moment(time, "HH:mm:ss").format("h:mm A");
  }
};

export const isSlotAfterNowInLocationTimezone = (slot, locationTimezone) => {
  if (!slot?.date || !slot?.starttime) return false;

  try {
    let slotMoment;
    if (locationTimezone) {
      slotMoment = parseTzSlot(slot.date, slot.starttime, locationTimezone);
    } else {
      slotMoment = dayjs(`${formateChangeUnderScore(slot.date)}T${slot.starttime}`);
    }

    if (!slotMoment.isValid()) return false;

    const nowInLocation = locationTimezone ? dayjs().tz(locationTimezone) : dayjs();
    return slotMoment.isAfter(nowInLocation);
  } catch {
    return false;
  }
};



export const isSlotAfterPreviousAppointment = (
  slot,
  previousSeletedSlots,
  locationTimezone,
) => {
  if (!slot?.date || !slot?.starttime) return false;

  try {
    // Initial load - use current logic
    if (!previousSeletedSlots) {
      return isSlotAfterNowInLocationTimezone(slot, locationTimezone);
    }

    const parseSlot = (date, time) => {
      const dateTime = `${formateChangeUnderScore(date)}T${time}`;

      return locationTimezone
        ? dayjs.tz(dateTime, locationTimezone)
        : dayjs(dateTime);
    };

    const previousSlotMoment = parseSlot(
      previousSeletedSlots.date,
      previousSeletedSlots.starttime,
    );

    const currentSlotMoment = parseSlot(
      slot.date,
      slot.starttime,
    );
    if (
      !previousSlotMoment.isValid() ||
      !currentSlotMoment.isValid()
    ) {
      return false;
    }

    let nextAvailableTime = previousSlotMoment;
    const gapConfig = previousSeletedSlots.gapBtwAppt;

    if (gapConfig && typeof gapConfig.value === 'number' && gapConfig.value > 0) {
      let unit;
      switch (gapConfig.type) {
        case 'year':
          unit = 'year';
          break;
        case 'month':
          unit = 'month';
          break;
        case 'week':
          unit = 'week';
          break;
        case 'day':
        case 'days': // Handle both 'day' and 'days'
          unit = 'day';
          break;
        case 'hours':
          unit = 'hour';
          break;
        case 'minutes':
          unit = 'minute';
          break;
        default:
          unit = 'minute'; // Fallback to minute if type is unknown or not specified
      }
      nextAvailableTime = previousSlotMoment.add(gapConfig.value, unit);
    }

    // Allow exact next slot time also
    return (
      currentSlotMoment.isSame(nextAvailableTime) ||
      currentSlotMoment.isAfter(nextAvailableTime)
    );
  } catch (error) {
    console.error("Slot filtering error:", error);
    return false;
  }
};

export const formateChange24Hour = (value) => {
  return moment(value, "hh:mm A").format("HH:mm:ss");
};
export const formateChange12Hour = (value) => {
  return moment(value, "HH:mm:ss").format("h:mm A");
};
export const TodayDate = () => {
  return moment().format("YYYY-MM-DD");
};

export const formatChangePoint = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/[:\\-]/g, "."); // Removed the escape for the forward slash
};
export const YearGet = (value) => {
  return moment(value).format("YYYY");
};
export const AthenaDateformat = (value) => {
  return moment(value).format("M/D/YYYY");
};

// Oldest date of birth accepted on any DOB field, for every client. Anything older is treated as a
// typo (e.g. a birth year of 1685) and blocked in the form before it reaches the Setter API / EHR.
export const DOB_MAX_AGE_YEARS = 120;

// Earliest selectable DOB, for the date picker's minDate prop.
export const getDobMinDate = () =>
  dayjs().subtract(DOB_MAX_AGE_YEARS, "year").startOf("day");

// Formats a DOB may arrive in, parsed strictly (no rollover of e.g. 2/30):
// - M/D/YYYY, MM/DD/YYYY: date picker output (AthenaDateformat) and US-style stored values
// - YYYY-MM-DD, YYYY-M-D: ISO-style values from the API / EHR
// - M-D-YYYY, MM-DD-YYYY: dash-separated US dates from insurance-card scans
// - M/D/YY, MM/DD/YY: two-digit years from insurance-card scans (see parseDob for the century rule)
const DOB_FORMATS = [
  "M/D/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "YYYY-M-D", "M-D-YYYY", "MM-DD-YYYY", "M/D/YY",
  "MM/DD/YY",
];

// Parses a DOB in any accepted format into a moment, or returns null when it can't be read.
// Shared by getDobError and normalizeDob so the date that is checked is the date that is saved.
const parseDob = (value) => {
  // Trim strings before strict parsing — a scanned insurance-card DOB is set untrimmed and
  // "01/02/1980 " would otherwise fail. Non-strings (Date/moment) are passed through unchanged.
  const input = typeof value === "string" ? value.trim() : value;
  const trimmed = String(input);
  let dob = moment(input, DOB_FORMATS, true);
  // Fall back to native parsing only for non-numeric text (e.g. insurance-card OCR "Jan 2, 1980");
  // numeric dates must match DOB_FORMATS exactly so impossible days like 2/30 are not rolled over.
  const isNumericDate = /^\d{1,4}[/-]\d{1,2}[/-]\d{1,4}$/.test(trimmed);
  if (!dob.isValid() && !isNumericDate) dob = moment(new Date(input));
  if (!dob.isValid()) return null;
  // Two-digit years (e.g. "01/02/55" from a scanned card): moment reads 00-68 as 20xx. A DOB can't
  // be in the future, so a 20xx date after today is read as 19xx instead. Years up to today stay
  // 20xx — e.g. "01/02/26" is 2026 and "01/02/15" is 2015 — because the patient may be a child, and
  // reading those as 19xx would silently save the wrong year with no error shown.
  if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(trimmed) && dob.isAfter(moment(), "day")) {
    dob = dob.clone().subtract(100, "years");
  }
  return dob;
};

// Returns "" when the DOB is empty or valid, otherwise the message to show under the field.
// Empty values are left to each form's own "required" message.
export const getDobError = (value) => {
  if (!value) return "";
  const dob = parseDob(value);
  if (!dob) return "Please enter a valid date of birth";
  if (dob.isAfter(moment(), "day")) return "Date of birth cannot be in the future";
  if (dob.isBefore(getDobMinDate().toDate(), "day")) return "Please enter a valid date of birth";
  return "";
};

// Numeric dates that already carry a four-digit year (M/D/YYYY, M-D-YYYY, YYYY-MM-DD, or an ISO
// timestamp like "1980-01-02T00:00:00.000Z"). Their year can't be misread, so they are kept as-is.
const FULL_YEAR_DOB = /^(\d{1,2}[/-]\d{1,2}[/-]\d{4}|\d{4}-\d{1,2}-\d{1,2}(T.*)?)$/;

// Rewrites a valid DOB whose year is ambiguous — a two-digit year or card-scan text like
// "Jan 2, 1980" — as full M/D/YYYY (AthenaDateformat's format), so the value stored and sent to the
// API is the date getDobError checked: a scanned "01/02/30" becomes "1/2/1930", not "01/02/30".
// Empty/invalid values are returned unchanged (so the field still shows its error). Full-year dates
// are only trimmed, so existing stored values are sent as before (re-parsing an ISO timestamp in
// local time could also shift it a day).
export const normalizeDob = (value) => {
  if (!value || getDobError(value)) return value;
  if (typeof value !== "string") return value;
  // Trim so a scanned "01/02/1980 " (checked trimmed by getDobError) is not stored/sent untrimmed.
  const trimmed = value.trim();
  if (FULL_YEAR_DOB.test(trimmed)) return trimmed;
  return parseDob(trimmed).format("M/D/YYYY");
};

// While the patient is still typing, the date picker reports in-progress values: "Invalid date"
// until every section is filled, and partial years like 0001 / 0019 / 0199 on the way to 1990.
export const isDobIncomplete = (value) => {
  if (!value || value === "Invalid date") return true;
  const year = String(value).trim().match(/^\d{1,2}\/\d{1,2}\/(\d{4})$/)?.[1];
  return Boolean(year) && Number(year) < 1000;
};

// Use on change: no error while the DOB is still being typed; submit still runs getDobError().
export const getDobTypingError = (value) =>
  isDobIncomplete(value) ? "" : getDobError(value);
export const formatChangeLetters = (value) => {
  return moment(value, "YYYY/MM/DD").format("ddd MMM D");
};
export const formatChangeLetterLayline = (value) => {
  return moment(value, "MM/DD/YYYY").format("ddd MMM D");
};
export const formatDurationAdjective = (minutes) => {
  const total = Number(minutes);
  if (!total || total <= 0) return "";
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  const parts = [];
  if (hours) parts.push(`${hours}-hour`);
  if (mins) parts.push(`${mins}-min`);
  return parts.join(" ");
};
export const formatDurationMinutes = (minutes, appendLabel = "") => {
  const total = Number(minutes);
  if (!total || total <= 0) return "";
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (appendLabel) return `${formatDurationAdjective(minutes)} ${appendLabel}`;
  if (hours && mins) return `${hours} hour${hours > 1 ? "s" : ""} ${mins} min`;
  if (hours) return `${hours} hour${hours > 1 ? "s" : ""}`;
  return `${mins} min${mins > 1 ? "s" : ""}`;
};
export const formatChangeDayDateLayline = (value) => {
  return moment(value, "MM/DD/YYYY").format("ddd D");
};
export const formatChangeDate = (value) => {
  return moment(value, "YYYY/MM/DD").format("YYYY/MM/DD");
};
export const formatChangeDateSlash = (value) => {
  return moment(value).format("YYYY-MM-DD");
};
export const formatDateToDash = (value) => {
  return moment(value).format("M-D-YYYY");
};
export const formatChangeDays = (value) => {
  return moment(value).fromNow();
};

export const formateChangeUnderScore = (value) => {
  return moment(value).format("YYYY-MM-DD");
};
export const dateFormate = (value) => {
  return moment(value).format("MMM DD YYYY");
};
export const payloadDateFormat = (value) => {
  return moment(value).format("MM/DD/YYYY");
};
export const dateWithTimeFormate = (value) => {
  return moment(value).format("M/D/YYYY h:mm A");
};
export const dateWithMonthFormate = (value) => {
  return moment(value).format('MMMM D, YYYY');
};
export const removeWhiteSpace = (str) => {
  if (!str) return "";
  const value = str?.toLowerCase()?.replace(/\s+/g, "");
  return value;
};

// export function Datechange(datetimeStr) {
//   // Parse the original date using moment
//   const originalDate = moment(datetimeStr, "MM/DD/YYYY HH:mm");

//   // Adjust to the desired date (Sep 10, 2024, at 10:00 PM)
//   const adjustedDate = moment("2024-09-10 22:00", "YYYY-MM-DD HH:mm");

//   // Format the adjusted datetime
//   const formattedDate = adjustedDate.format("MMM DD, @ hh:mm A YYYY");

//   return formattedDate;
// }
export const formatChangeNumber = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/[a-zA-Z!@#$%^&*()_+=[\]{};':"\\|,.<>/?`~ -]/g, "");
};

export const handlePaginationFilter = (cardcount = 6, page, sorted) => {
  const count = cardcount;
  const startIndex = count * page - count;
  const endIndex = startIndex + count - 1;

  const fitlerPagination = sorted.filter(
    (v, i) => startIndex <= i && endIndex >= i
  );
  return fitlerPagination;
};

export const UniqueNumber = () => {
  return Date.now();
};

export const previousTimeCheck = (time, date) => {
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();
  const currentHours = new Date().getHours();
  const currentMin = new Date().getMinutes();
  const currentYear = new Date().getFullYear();
  const [years, month, day] = formatChangeDate(date).split("/");

  const time24hr = formateChange24Hour(time);
  const previousTimeDiabled =
    Number(month) === Number(currentMonth) &&
    Number(day) === Number(currentDay) &&
    Number(currentYear) === Number(years) &&
    parseFloat(formatChangePoint(time24hr)) <=
      parseFloat(formatChangePoint(`${currentHours}:${currentMin}`));
  return previousTimeDiabled;
};
export const generateWeekDates = (currentDate) => {
  // Calculate the start and end of the current week (Monday - Sunday)
  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const endDate = endOfWeek(currentDate, { weekStartsOn: 1 });

  const weekDates = [];

  // Generate dates from Monday to Sunday
  for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
    const formattedDate = moment(date).format("MM/DD/YYYY");
    const dayOfWeek = format(date, "EEE");

    const isAvailable = !(dayOfWeek === "Sat" || dayOfWeek === "Sun");

    // Only include dates from Monday to Friday
    if (isAvailable) {
      weekDates.push({
        day: dayOfWeek,
        date: formattedDate,
        isAvailable,
      });
    }
  }

  return weekDates;
};
export function formatDateRange(date1, date2) {
  const startDate = moment(date1);
  const endDate = moment(date2);

  // Check if both dates are in the same month
  if (startDate.isSame(endDate, "month")) {
    return startDate.format("MMM-YYYY"); // Return "Sep-2024" format if dates are in the same month
  } else if (startDate.isSame(endDate, "year")) {
    // Return "Sep-Oct-2024" format if dates are in different months
    return `${startDate.format("MMM")}-${endDate.format(
      "MMM"
    )}-${startDate.format("YYYY")}`;
  } else {
    // Return "Dec-2024-Jan-2025" for different months and years
    return `${startDate.format("MMM-YYYY")}-${endDate.format("MMM-YYYY")}`;
  }
}
export const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formateWeeks = (date, formate = "ddd") => {
  return moment(date).format(formate);
};
export const formateDay = (date) => {
  return moment(date).format("DD");
};
export const formateMonth = (date) => {
  return moment(date).format("MMM");
};

export const CheckTimeAMorPM = (value) => {
  const time = moment(value, "HH:mm");
  return time.format("A") === "AM";
};
